import { useMemo } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { PageChrome, ROUTES, useIsDesktop } from '@/components/gg/chrome'
import { useAlerts, useDismissAlert, useWatchState, alertHeadline } from '@/hooks/useAlerts'
import { AlertCard, AlertTag, BudgetBadge, WatchEmpty } from '@/components/alerts/AlertBits'
import { SplitQueue } from '@/components/alerts/SplitQueue'
import { asOf, useQueueBuild } from '@/components/alerts/ui'
import type { Alert } from '@/components/alerts/types'
import '@/styles/alerts.css'

/**
 * Alerts (/app/alerts). Root tab. Brian's alerts.html v1.5 and desk-split.js.
 *
 * The queue is the feature's proof: coming back after a week to a stack of
 * dated alerts shows the watches ran the whole time. Nothing expires. Each
 * alert already used 1 seed from the alert budget when it landed; Build is the
 * normal story, 12 from the week. Dismiss moves it to Earlier.
 *
 * Phone: cards; tap one for the single alert. Desktop: the list on the left,
 * the chosen alert on the right (J/K, B, X), and the next one opens after an
 * action. ?refill=1 shows the empty budget (watch-empty.html).
 */
export function AlertsPage() {
  const desk = useIsDesktop()
  const nav = useNavigate()
  const [q] = useSearchParams()
  const { data: w } = useWatchState()
  const { data: alerts, isLoading } = useAlerts()
  const dismiss = useDismissAlert()
  const qb = useQueueBuild(alertHeadline, [['alerts'], ['watch-state']])

  const waiting = useMemo(() => (alerts ?? []).filter((a) => a.status === 'new').sort((a, b) => Date.parse(b.broke_at) - Date.parse(a.broke_at)), [alerts])
  const done = useMemo(() => (alerts ?? []).filter((a) => a.status !== 'new').sort((a, b) => Date.parse(b.done_at ?? b.delivered_at) - Date.parse(a.done_at ?? a.delivered_at)), [alerts])

  const chrome = <PageChrome title="Alerts" navKey="alerts" actions={<Link className="gg-act" to={ROUTES.breaking}>Manage watches</Link>} />

  if (q.get('refill')) return <>{chrome}<div className="gg gga"><WatchEmpty /></div></>

  const doDismiss = (a: Alert) => { dismiss.mutate(a.id); qb.toast.show(<>Dismissed. <b>It stays under Earlier.</b></>) }
  const doBuild = (a: Alert) => qb.build(a.id, () => qb.toast.show(<>Building. <b>Emma is writing it; it lands in your Library.</b></>))

  const note = w && (w.bank === 0
    ? <div className="gga-note empty"><span>Your alert budget is empty. Matching has stopped; what’s here is still yours to build.</span><Link to={`${ROUTES.alerts}?refill=1`}>Refill</Link></div>
    : w.paused ? <div className="gga-note paused">Paused. Nothing new is matched; these stay buildable.</div> : null)

  const empty = <div className="gga-none"><b>Nothing waiting.</b>Your watches are running. When a match lands it waits here until you build or dismiss it.</div>
  const n = waiting.length
  const cost = n ? <p className="gga-cost">Each of these used 1 seed from your alert budget when it landed. {n} {n === 1 ? 'alert, 1 seed' : `alerts, ${n} seeds`}, exactly what the budget is for.</p> : null
  const earlier = done.length > 0 && (
    <details className="gga-earlier">
      <summary>Earlier · {done.length}</summary>
      {done.map((a) => (
        <div className="row" key={a.id}>
          <span className={`st${a.status === 'built' ? ' built' : ''}`}>{a.status === 'built' ? 'Built' : 'Dismissed'}</span>
          {a.status === 'built' && a.story_id ? <Link to={ROUTES.story(a.story_id)}>{a.headline}</Link> : <Link to={ROUTES.alert(a.id)}>{a.headline}</Link>}
        </div>
      ))}
    </details>
  )

  return (
    <>
      {chrome}
      <div className={`gg gga${desk ? ' gga--wide' : ''}`}>
        <div className="gga-head">
          <div className="sub">{isLoading ? 'Loading' : n ? `${n} waiting · nothing expires` : 'Nothing waiting · nothing expires'}</div>
          {w && <BudgetBadge w={w} onOpen={() => nav(ROUTES.breaking)} />}
        </div>
        {note}
        {desk ? (
          <SplitQueue items={waiting} cost={qb.cost}
            card={(a, sel, select) => <AlertCard key={a.id} a={a} selected={sel} cost={qb.cost} onOpen={select} onDismiss={() => doDismiss(a)} onBuild={() => doBuild(a)} />}
            detail={(a) => (
              <>
                <AlertTag a={a} />
                <h2>{a.headline}</h2>
                <div className="by">{a.source_url ? <a href={a.source_url} target="_blank" rel="noopener noreferrer">{a.outlet}</a> : <b>{a.outlet}</b>} · {asOf(a.broke_at)}</div>
                {a.dek && <p>{a.dek}</p>}
                <div className="meta">Used 1 seed from your alert budget when it landed. Build writes it like any story.</div>
              </>
            )}
            onBuild={doBuild} onDismiss={doDismiss}
            after={<>{!n && !isLoading && empty}{cost}{earlier}</>}
          />
        ) : (
          <>
            {waiting.map((a) => <AlertCard key={a.id} a={a} cost={qb.cost} onOpen={() => nav(ROUTES.alert(a.id))} onDismiss={() => doDismiss(a)} onBuild={() => doBuild(a)} />)}
            {!n && !isLoading && empty}
            {cost}
            {earlier}
          </>
        )}
      </div>
      {qb.confirm}
      {qb.toast.node}
    </>
  )
}
